/**
 * decorationLifecycle.js -- ADR 0056 (per-store Decorations tab).
 *
 * Canonical 8-state lifecycle-chip vocabulary for the store Decorations
 * readiness rollup (StoreDecorations.vue). Matches the design handoff
 * (planning/design-handoffs/handoff_2026-07-09/2026-07-09-decorations-tab/
 * design/decorations-store.jsx LIFECYCLE table) label-for-label,
 * color-for-color -- ADR 0056 states "colors/type/spacing/copy/grouping/
 * chip-language are FINAL intent."
 *
 * The 8 states map 1:1 to the backend's ``lifecycle_state`` string on
 * GET /api/stores/<id>/decoration_rollup/ (PR #1986):
 *   draft, awaiting_digitization, in_digitization,
 *   awaiting_client_approval, changes_requested, approved, in_use,
 *   archived.
 *
 * NOTE: the design JSX keys use hyphens ("awaiting-digitization"); the
 * backend wire format uses underscores ("awaiting_digitization") per
 * the dispatch contract. This module is keyed on the backend's
 * underscore form -- the single source every consumer of this page
 * reads.
 *
 * "Changes requested" is the canonical negative outcome (ADR 0040);
 * there is no routine "Rejected" state in this rollup.
 *
 * A pure table (not a component method) so it's easily unit-tested and
 * shared between the "Needs attention" rows, the "Approved & in use"
 * rows, and the "How decorations work" explainer's full 8-chip legend.
 */

export const LIFECYCLE = Object.freeze({
  draft: {
    label: "Draft",
    icon: "edit_note",
    bg: "#eef0f3",
    fg: "#757575", // --tf-fg-4
  },
  awaiting_digitization: {
    label: "Awaiting digitization",
    icon: "schedule",
    bg: "#fff8e1",
    fg: "#8a6d3b",
  },
  in_digitization: {
    label: "In digitization",
    icon: "autorenew",
    bg: "#fff8e1",
    fg: "#8a6d3b",
  },
  awaiting_client_approval: {
    label: "Awaiting client approval",
    icon: "pending_actions",
    bg: "#eaf4fc",
    fg: "#119ae9", // --tf-primary
  },
  changes_requested: {
    label: "Changes requested",
    icon: "error_outline",
    bg: "#fdecec",
    fg: "#ef0405", // --tf-red
  },
  approved: {
    label: "Approved",
    icon: "check_circle",
    bg: "#e6f7ed",
    fg: "#2e7d32", // --tf-success-dark
  },
  in_use: {
    label: "In use",
    icon: "check_circle",
    bg: "#e6f7ed",
    fg: "#2e7d32",
  },
  archived: {
    label: "Archived",
    icon: "archive",
    bg: "#f5f5f5",
    fg: "#9e9e9e", // --tf-fg-5
  },
});

// Display order for the "How decorations work" explainer's full-legend
// chip row. Matches the design JSX's LIFECYCLE_ORDER exactly.
export const LIFECYCLE_ORDER = Object.freeze([
  "draft",
  "awaiting_digitization",
  "in_digitization",
  "awaiting_client_approval",
  "changes_requested",
  "approved",
  "in_use",
  "archived",
]);

// A decoration is "ready" (approved & in use) when its state is one of
// these two. Per ADR 0041's shared readiness rule, this MUST stay in
// sync with the readiness helper the editor / Products list / Review &
// Launch all use -- both are driven by the same two backend state
// strings, so there is nothing to fork here.
export const READY_STATES = Object.freeze(["approved", "in_use"]);

// Everything else needs attention before launch. ADR 0056 leaves
// "archived" out of both groups deliberately (the open question in the
// ADR resolves to "currently omitted" -- an archived decoration that
// was once used in this store is not surfaced by this rollup).
export const NEEDS_ATTENTION_STATES = Object.freeze([
  "draft",
  "awaiting_digitization",
  "in_digitization",
  "awaiting_client_approval",
  "changes_requested",
]);

/**
 * Chip metadata for a lifecycle_state value. Falls back to a neutral
 * unknown-state chip (rather than throwing) so an unexpected backend
 * value never crashes the page -- defensive default matching the
 * design JSX's own fallback.
 */
export function chipFor(state) {
  return (
    LIFECYCLE[state] || {
      label: state || "Unknown",
      icon: "circle",
      bg: "#eef0f3",
      fg: "#757575",
    }
  );
}

export function isReady(state) {
  return READY_STATES.includes(state);
}

export function needsAttention(state) {
  return NEEDS_ATTENTION_STATES.includes(state);
}

export default {
  LIFECYCLE,
  LIFECYCLE_ORDER,
  READY_STATES,
  NEEDS_ATTENTION_STATES,
  chipFor,
  isReady,
  needsAttention,
};
